
import React from "react";
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Star } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

interface Therapist {
  id: number;
  name: string;
  specialization: string;
  avatarUrl: string;
  profileUrl: string;
  rating: number;
  reviewCount: number;
  bio: string;
  services: string[];
}

const FeaturedTherapists = ({ therapists }: { therapists: Therapist[] }) => {
  const { t } = useTranslation('homepage');
  const { t: tPractitioners } = useTranslation('practitioners');
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section 
      ref={ref}
      className={`py-12 bg-white transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      data-testid="featured-therapists"
    >
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-display font-semibold text-warm-800 mb-8 text-center">
          {t('featuredPractitioners')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 featured-practitioners">
          {therapists.map((therapist, index) => (
            <Card 
              key={therapist.id} 
              className={`overflow-hidden hover:shadow-md transition-all duration-500 w-full bg-warm-50 border border-warm-200 shadow-sm ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center space-x-4">
                  <Avatar className="h-12 w-12 rounded-full flex-shrink-0">
                    <AvatarImage 
                      src={therapist.avatarUrl} 
                      alt={therapist.name}
                      className="object-cover" 
                    />
                    <AvatarFallback>{therapist.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col flex-1">
                    <CardTitle className="text-lg font-medium truncate">{therapist.name}</CardTitle>
                    <CardDescription>{therapist.specialization}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center mt-2 text-sm">
                  <div className="flex items-center">
                    <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
                    <span className="ml-1 text-warm-800 font-medium">{therapist.rating}</span>
                  </div>
                  <span className="mx-1 text-warm-500">•</span>
                  <span className="text-warm-500">{therapist.reviewCount} {tPractitioners('reviews')}</span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-warm-600 mb-3 line-clamp-3">{therapist.bio}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {therapist.services.map((service, index) => (
                    <Badge key={index} variant="outline" className="bg-brand-100 text-brand-700 hover:bg-brand-200 border-brand-200">
                      {service}
                    </Badge>
                  ))}
                </div>
                <Link to={therapist.profileUrl} className="text-brand-600 hover:text-brand-700 underline font-medium">
                  {tPractitioners('viewProfile')}
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedTherapists;
